import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { autoBlockText, ticketXdrActionsQuery, ticketXdrQuery, type TicketRespondAction, type TicketXdrDetection, type TicketXdrScope } from '../api/securitee'
import { ResponseActionsList } from '../securitee/ResponseActionsList'
import { severityLabel } from '../securitee/columns'
import { Spinner } from '../ui/Spinner'
import {
  activeFilterCount,
  applyFilters,
  DEFAULT_PAGE_SIZE,
  EMPTY_FILTERS,
  mitreOptions,
  PAGE_SIZES,
  pageCount,
  pageSlice,
  ruleOptions,
  type PageSize,
  type XdrFilters,
} from './xdrDetectionFilter'
import { RESPOND_ACTIONS, TicketXdrRespond, type RespondSuggest } from './TicketXdrRespond'
import { XdrSuppress } from './XdrSuppress'
import { XdrChain } from './XdrChain'
import { XdrTimeline } from './XdrTimeline'
import './TicketXdrTab.css'
import { NEW_TAB } from '../ui/NewTab'

/**
 * Fila "XDR" a tichetului unui incident (docs XDR 05, PR 2).
 *
 * Analistul lucreaza incidentul din tichet, fara sa sara in Securitee: sus
 * rezumatul incidentului, dedesubt detectiile lui. Clic pe o detectie o
 * desface: regula, MITRE si evenimentul Windows din care a pornit, camp cu
 * camp - linia de comanda, utilizatorul, procesul.
 *
 * "Workstation, 30 days" arata tot ce a venit de pe aparat in ultimele 30 de
 * zile, ca sa se vada ce s-a mai intamplat inainte; detectiile altor incidente
 * sunt mai stinse si duc la tichetul lor.
 *
 * Jos, sub detectii, banda "Response actions": tot ce s-a facut pe aparat, pas
 * cu pas, langa butoanele Respond din care pleaca - oricare detectie ar fi
 * deschisa. In desfacerea detectiei, `Respond` (PR 3): actiunile pe aparat.
 */
export function TicketXdrTab({ id }: { id: number }) {
  const [scope, setScope] = useState<TicketXdrScope>('incident')
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set())
  const [filters, setFilters] = useState<XdrFilters>(EMPTY_FILTERS)
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  // la comutare ramane lista veche pe ecran pana vine cea noua, fara salt
  const query = useQuery({ ...ticketXdrQuery(id, scope), placeholderData: keepPreviousData })

  // alt scope = alt set de date, alt context: filtrele si pagina o iau de la capat
  useEffect(() => {
    setFilters(EMPTY_FILTERS)
    setPage(1)
  }, [scope])

  const all = query.data?.detections ?? []
  const filtered = useMemo(() => applyFilters(all, filters), [all, filters])

  if (query.isPending) return <Spinner />

  if (query.isError) {
    return <p className="color-red">{query.error instanceof Error ? query.error.message : 'The XDR incident could not be read.'}</p>
  }

  const { incident, detections, days, limit } = query.data

  const pages = pageCount(filtered.length, pageSize)
  const current = Math.min(page, pages)
  const paged = pageSlice(filtered, pageSize, current)

  const toggle = (detection: number) =>
    setOpen((was) => {
      const next = new Set(was)

      if (!next.delete(detection)) next.add(detection)

      return next
    })

  const device = incident.device_name || `Device #${incident.device_id}`

  return (
    <div className="xdr-tab">
      <div className="xdr-summary">
        <div className="xdr-summary-head">
          <Severity severity={incident.severity} />
          <span className="xdr-device-chip">
            <i className="fa fa-desktop" aria-hidden="true"></i> <span className="xdr-device">{device}</span>
          </span>
          <span className="xdr-summary-title">{incident.title}</span>
        </div>
        <div className="xdr-meta">
          <span className="xdr-mono">
            {incident.first_detected_at} – {incident.last_detected_at} UTC
          </span>
          <span className="xdr-chip">
            {incident.detections} {incident.detections === 1 ? 'detection' : 'detections'}
          </span>
          <a className="btn btn-xs btn-default xdr-open-securitee" href="/app/securitee/incidents" {...NEW_TAB}>
            Open in Securitee <i className="fa fa-external-link-alt" aria-hidden="true"></i>
          </a>
        </div>
      </div>

      <div className="xdr-scope-bar">
        <div className="btn-group btn-group-sm xdr-scope" role="group" aria-label="Detections to show">
          <ScopeButton scope="incident" current={scope} onPick={setScope}>This incident</ScopeButton>
          <ScopeButton scope="device" current={scope} onPick={setScope}>Workstation, {days} days</ScopeButton>
        </div>
        {query.isFetching && <span className="xdr-loading font-grey-cascade">Loading...</span>}
      </div>

      {detections.length === 0 ? (
        <p className="xdr-empty font-grey-cascade">
          {scope === 'device' ? `No detections from this workstation in the last ${days} days.` : 'No detections on this incident.'}
        </p>
      ) : (
        <>
          <DetectionFilters
            detections={detections}
            filters={filters}
            onChange={(next) => {
              setFilters(next)
              setPage(1)
            }}
            shown={filtered.length}
            total={detections.length}
          />
          {filtered.length === 0 ? (
            <p className="xdr-empty font-grey-cascade xdr-no-match">No detections match the filters.</p>
          ) : (
            <div className="xdr-card xdr-detections-card">
              <div className="xdr-detections-head" aria-hidden="true">
                <span>Time (UTC)</span>
                <span>Severity</span>
                <span>Detection</span>
                <span>MITRE ATT&amp;CK</span>
                <span>Rule</span>
              </div>
              <ul className="list-unstyled xdr-detections">
                {paged.map((d) => (
                  <DetectionRow
                    key={d.ID}
                    ticket={id}
                    detection={d}
                    canRespond={incident.status === 'open'}
                    canSuppress={query.data.can_suppress === true}
                    expanded={open.has(d.ID)}
                    onToggle={() => toggle(d.ID)}
                  />
                ))}
              </ul>
              <Pager
                total={filtered.length}
                pages={pages}
                page={current}
                pageSize={pageSize}
                onPage={setPage}
                onPageSize={(size) => {
                  setPageSize(size)
                  setPage(1)
                }}
              />
            </div>
          )}
        </>
      )}
      {scope === 'device' && detections.length >= limit && (
        <p className="font-grey-cascade">Showing the latest {limit} detections.</p>
      )}

      <section className="xdr-card xdr-actions xdr-actions-card">
        <div className="xdr-card-body">
          <ResponseActionsList query={ticketXdrActionsQuery(id)} />
        </div>
      </section>
    </div>
  )
}

function ScopeButton({ scope, current, onPick, children }: { scope: TicketXdrScope; current: TicketXdrScope; onPick(s: TicketXdrScope): void; children: ReactNode }) {
  const active = scope === current

  return (
    <button type="button" className={`btn ${active ? 'btn-primary' : 'btn-default'}`} aria-pressed={active} onClick={() => onPick(scope)}>
      {children}
    </button>
  )
}

const SEVERITIES = ['low', 'medium', 'high', 'critical'] as const

/**
 * Bara de filtre peste lista de detectii: gravitate, regula, MITRE, cautare,
 * sortare pe timp, "doar curente". Toate restrang impreuna (SI). Totul in
 * browser, pe cele cel mult 200 de detectii aduse - vezi xdrDetectionFilter.
 */
function DetectionFilters({
  detections,
  filters,
  onChange,
  shown,
  total,
}: {
  detections: readonly TicketXdrDetection[]
  filters: XdrFilters
  onChange(next: XdrFilters): void
  shown: number
  total: number
}) {
  const rules = useMemo(() => ruleOptions(detections), [detections])
  const mitre = useMemo(() => mitreOptions(detections), [detections])
  const active = activeFilterCount(filters)

  const toggleIn = (set: ReadonlySet<string>, value: string): Set<string> => {
    const next = new Set(set)

    if (!next.delete(value)) next.add(value)

    return next
  }

  return (
    <div className="xdr-filters" role="group" aria-label="Filter detections">
      <div className="xdr-filter-severities btn-group btn-group-sm" role="group" aria-label="Severity">
        {SEVERITIES.map((sev) => {
          const [label] = severityLabel(sev)
          const on = filters.severities.has(sev)

          return (
            <button
              key={sev}
              type="button"
              className={`btn btn-default xdr-sev-pill xdr-sev-${sev}${on ? ' active' : ''}`}
              aria-pressed={on}
              onClick={() => onChange({ ...filters, severities: toggleIn(filters.severities, sev) })}
            >
              {label}
            </button>
          )
        })}
      </div>

      <select
        className="form-control input-sm xdr-filter-rule"
        aria-label="Rule"
        value={[...filters.rules][0] ?? ''}
        onChange={(e) => onChange({ ...filters, rules: e.target.value === '' ? new Set() : new Set([e.target.value]) })}
      >
        <option value="">All rules</option>
        {rules.map((r) => (
          <option key={r.value} value={r.value}>
            {r.value} · {r.label} ({r.count})
          </option>
        ))}
      </select>

      <select
        className="form-control input-sm xdr-filter-mitre"
        aria-label="MITRE technique"
        value={[...filters.mitre][0] ?? ''}
        onChange={(e) => onChange({ ...filters, mitre: e.target.value === '' ? new Set() : new Set([e.target.value]) })}
      >
        <option value="">All MITRE</option>
        {mitre.map((m) => (
          <option key={m.value} value={m.value}>
            {m.value} ({m.count})
          </option>
        ))}
      </select>

      <input
        type="search"
        className="form-control input-sm xdr-filter-text"
        placeholder="Search title or rule id"
        aria-label="Search detections"
        value={filters.text}
        onChange={(e) => onChange({ ...filters, text: e.target.value })}
      />

      <select
        className="form-control input-sm xdr-filter-order"
        aria-label="Sort by time"
        value={filters.order}
        onChange={(e) => onChange({ ...filters, order: e.target.value === 'oldest' ? 'oldest' : 'newest' })}
      >
        <option value="newest">Newest first</option>
        <option value="oldest">Oldest first</option>
      </select>

      <label className="xdr-filter-current">
        <input
          type="checkbox"
          checked={filters.onlyCurrent}
          onChange={(e) => onChange({ ...filters, onlyCurrent: e.target.checked })}
        />{' '}
        Only this incident
      </label>

      <span className="xdr-filter-count font-grey-cascade">
        {shown === total ? `${total} detections` : `${shown} of ${total}`}
      </span>

      {active > 0 && (
        <button type="button" className="btn btn-xs btn-link xdr-filter-clear" onClick={() => onChange(EMPTY_FILTERS)}>
          Clear filters
        </button>
      )}
    </div>
  )
}

/** Selector de marime (10/25/50/100/All) si pagini numerotate, sub lista. */
function Pager({
  total,
  pages,
  page,
  pageSize,
  onPage,
  onPageSize,
}: {
  total: number
  pages: number
  page: number
  pageSize: PageSize
  onPage(p: number): void
  onPageSize(s: PageSize): void
}) {
  const numbers = pageWindow(page, pages)

  return (
    <div className="xdr-pager">
      <label className="xdr-pager-size">
        Show{' '}
        <select
          className="form-control input-sm"
          aria-label="Detections per page"
          value={String(pageSize)}
          onChange={(e) => onPageSize(e.target.value === 'all' ? 'all' : (Number(e.target.value) as PageSize))}
        >
          {PAGE_SIZES.map((s) => (
            <option key={s} value={String(s)}>
              {s === 'all' ? 'All' : s}
            </option>
          ))}
        </select>{' '}
        per page
      </label>

      {pages > 1 && (
        <ul className="pagination pagination-sm xdr-pager-pages">
          <li className={page <= 1 ? 'disabled' : ''}>
            <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}>
              ‹
            </button>
          </li>
          {numbers.map((n, i) =>
            n === 0 ? (
              <li key={`gap-${i}`} className="disabled xdr-pager-gap">
                <span>…</span>
              </li>
            ) : (
              <li key={n} className={n === page ? 'active' : ''}>
                <button type="button" aria-current={n === page ? 'page' : undefined} onClick={() => onPage(n)}>
                  {n}
                </button>
              </li>
            ),
          )}
          <li className={page >= pages ? 'disabled' : ''}>
            <button type="button" aria-label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}>
              ›
            </button>
          </li>
        </ul>
      )}

      <span className="xdr-pager-total font-grey-cascade">{total} shown</span>
    </div>
  )
}

/** Numerele de pagina de aratat: primele/ultimele si o fereastra in jurul celei curente; 0 = "…". */
function pageWindow(page: number, pages: number): number[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1)

  const out = new Set([1, pages, page, page - 1, page + 1])
  const sorted = [...out].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b)
  const withGaps: number[] = []

  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1] > 1) withGaps.push(0)
    withGaps.push(n)
  })

  return withGaps
}

function Severity({ severity }: { severity: string }) {
  const [label, cls] = severityLabel(severity)

  return <span className={`label ${cls}`}>{label}</span>
}

/** `canRespond`: incidentul e deschis (tichetul activ); rezolvat sau inchis, e terminat. */
/** `canSuppress`: rolul poate lucra tichetul - "Suppress similar" (docs XDR 02, F4). */
function DetectionRow({ ticket, detection: d, canRespond, canSuppress, expanded, onToggle }: { ticket: number; detection: TicketXdrDetection; canRespond: boolean; canSuppress: boolean; expanded: boolean; onToggle(): void }) {
  const detailId = `xdr-${ticket}-${d.ID}`
  // panoul Respond se incarca numai cand omul il cere
  const [respond, setRespond] = useState(false)
  // actiunea recomandata deschide Respond direct pe confirmarea ei (docs XDR 05, PR 6)
  const [preset, setPreset] = useState<TicketRespondAction | null>(null)
  // fiecare clic pe "(recommended)" e o cerere noua - si dupa un Cancel
  const [presetAsk, setPresetAsk] = useState(0)
  // AbuseIPDB tine deja IP-ul blocat pe aparat: nimic de recomandat
  const autoBlock = d.recommended.auto_block && d.recommended.target ? autoBlockText(d.recommended.target, d.recommended.auto_block) : null
  const recommended = autoBlock ? null : d.recommended.action
  // o singura unealta de investigatie deschisa: ce s-a intamplat, lantul proceselor, suprimarea
  const [tool, setTool] = useState<'story' | 'chain' | 'suppress' | null>(null)
  const pick = (t: 'story' | 'chain' | 'suppress') => setTool((v) => (v === t ? null : t))
  const respondButton = canRespond ? (
    <button
      type="button"
      className={`btn btn-sm ${recommended !== null ? 'btn-default' : 'btn-danger'} xdr-respond`}
      aria-expanded={respond}
      aria-controls={`${detailId}-respond`}
      onClick={() => {
        setPreset(null)
        setRespond((v) => !v)
      }}
    >
      <i className="fa fa-bolt" aria-hidden="true"></i> {recommended !== null ? 'Other actions' : 'Respond'}
    </button>
  ) : null

  return (
    <li className={`xdr-detection${d.current ? '' : ' xdr-other'}${expanded ? ' xdr-open' : ''}`}>
      <button type="button" className="xdr-detection-toggle" aria-expanded={expanded} aria-controls={detailId} onClick={onToggle}>
        <span className="xdr-time-cell">
          <i className={`fa fa-chevron-${expanded ? 'down' : 'right'} xdr-chevron`} aria-hidden="true"></i> <span className="xdr-time">{d.detected_at} UTC</span>
        </span>
        <span>
          <Severity severity={d.severity} />
        </span>
        <span className="xdr-title">{d.title}</span>
        <span>{d.mitre !== '' && <span className="xdr-mitre">{d.mitre}</span>}</span>
        <span className="xdr-rule">{d.rule_id}</span>
      </button>
      {!d.current && d.ticket_id !== null && (
        <a className="xdr-ticket-link" href={`/app/easeedesk/ticket/${d.ticket_id}`} {...NEW_TAB}>
          Ticket #{d.ticket_id}
        </a>
      )}
      {expanded && (
        <div id={detailId} className="xdr-detail">
          <div className={`xdr-recommended${recommended === null ? ' xdr-recommended-none' : ''}`}>
            <div className="xdr-recommended-text">
              {autoBlock ?? (
                <>
                  <span className="xdr-recommended-title">Recommended</span> {d.recommended.why}
                </>
              )}
            </div>
            {canRespond && recommended !== null && (
              <div className="xdr-recommended-buttons">
                <button
                  type="button"
                  className="btn btn-sm btn-danger xdr-recommended-action"
                  onClick={() => {
                    setPreset(recommended)
                    setPresetAsk((n) => n + 1)
                    setRespond(true)
                  }}
                >
                  <i className="fa fa-bolt" aria-hidden="true"></i> {RESPOND_ACTIONS[recommended].button} (recommended)
                </button>
                {respondButton}
              </div>
            )}
          </div>
          {recommended === null && respondButton}
          {!canRespond && <p className="font-grey-cascade xdr-respond-off">The incident is closed: no more response actions. Reopen the ticket to respond.</p>}
          {canRespond && respond && (
            <div id={`${detailId}-respond`} className="xdr-zone xdr-respond-zone">
              <TicketXdrRespond ticket={ticket} detection={{ ID: d.ID, title: d.title }} preset={preset} presetAsk={presetAsk} suggest={respondSuggest(d)} />
            </div>
          )}

          <div className="xdr-tools" role="group" aria-label="Investigate this detection">
            <ToolButton on={tool === 'story'} controls={`${detailId}-tool`} icon="fa-history" onClick={() => pick('story')}>
              What happened
            </ToolButton>
            <ToolButton on={tool === 'chain'} controls={`${detailId}-tool`} icon="fa-sitemap" onClick={() => pick('chain')}>
              Process chain
            </ToolButton>
            {canSuppress && (
              <ToolButton on={tool === 'suppress'} controls={`${detailId}-tool`} icon="fa-filter" onClick={() => pick('suppress')}>
                Suppress similar
              </ToolButton>
            )}
          </div>
          {tool !== null && (
            <div id={`${detailId}-tool`} className={`xdr-zone${tool === 'suppress' ? ' xdr-suppress' : ''}`}>
              {tool === 'story' && <XdrTimeline ticket={ticket} detection={d.ID} />}
              {tool === 'chain' && <XdrChain from={{ ticket }} detection={d.ID} />}
              {tool === 'suppress' && canSuppress && <XdrSuppress ticket={ticket} detection={d.ID} />}
            </div>
          )}

          <div className="xdr-facts">
            <section>
              <h5 className="xdr-caption">Detection rule</h5>
              <dl className="xdr-pairs">
                <dt>Rule</dt>
                <dd>{d.rule_id} (level {d.rule_level})</dd>
                {d.mitre !== '' && (
                  <>
                    <dt>MITRE ATT&amp;CK</dt>
                    <dd>{d.mitre}</dd>
                    <dt>Tactics</dt>
                    <dd>{d.mitre_tactics || '-'}</dd>
                    <dt>Techniques</dt>
                    <dd>{d.mitre_techniques || '-'}</dd>
                  </>
                )}
              </dl>
            </section>
            <section>
              <h5 className="xdr-caption xdr-event-head">Windows event</h5>
              {d.event === null ? (
                <p className="font-grey-cascade">No EDR event is linked to this detection.</p>
              ) : (
                <dl className="xdr-pairs">
                  <dt>Event</dt>
                  <dd>
                    {d.event.channel} / {d.event.event_id}
                  </dd>
                  <dt>Provider</dt>
                  <dd>{d.event.provider || '-'}</dd>
                  <dt>Computer</dt>
                  <dd>{d.event.computer || '-'}</dd>
                  <dt>Time (UTC)</dt>
                  <dd>{d.event.event_time}</dd>
                  {d.event.data.map((f) => (
                    <Field key={f.name} name={f.name} value={f.value} />
                  ))}
                </dl>
              )}
            </section>
          </div>
        </div>
      )}
    </li>
  )
}

function ToolButton({ on, controls, icon, onClick, children }: { on: boolean; controls: string; icon: string; onClick(): void; children: ReactNode }) {
  return (
    <button type="button" className={`btn btn-sm ${on ? 'btn-primary' : 'btn-default'} xdr-tool`} aria-pressed={on} aria-controls={controls} onClick={onClick}>
      <i className={`fa ${icon}`} aria-hidden="true"></i> {children}
    </button>
  )
}

function Field({ name, value }: { name: string; value: string }) {
  return (
    <>
      <dt>{name}</dt>
      <dd>
        <code className="xdr-value">{value === '' ? '-' : value}</code>
      </dd>
    </>
  )
}

/** Adresa sursei si contul din evenimentul detectiei, pentru Block IP / Disable account. */
export function respondSuggest(d: TicketXdrDetection): RespondSuggest {
  const field = (name: string) => {
    const value = d.event?.data.find((f) => f.name === name)?.value.trim() ?? ''

    return value === '-' ? '' : value
  }

  const { action, target } = d.recommended

  // tinta recomandata e luata de server din campul potrivit regulii (MemberName, SubjectUserName...)
  return {
    ip: action === 'block_ip' && target ? target : field('IpAddress'),
    user: action === 'disable_account' && target ? target : field('TargetUserName'),
  }
}
